import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { Switch, Tooltip } from 'antd'
import { useCallback, useMemo, useState } from 'react'
import type { IDetailCellRendererParams, SelectionChangedEvent } from 'ag-grid-community'
import { ComparisonCell } from './CellRenderers/ComparisonCell'
import dayjs from 'dayjs'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { ColDefBuilder } from './columnDefaults'
import { BolBaseTable } from './BolBaseTable'
import { DeleteEbolButton } from './DeleteEbolButton'
import { useModelConfig } from '@/hooks/useModelConfig'
import { SupplierCell } from './CellRenderers/SupplierCell'
import { ProductCell } from './CellRenderers/ProductCell'
import { TerminalCell } from './CellRenderers/TerminalCell'
import type { components } from '@/types/api/v1'

const isVolumeMatch = (
  { volume_amount_threshold, volume_percent_threshold }: Gravitate.EbolConfig,
  vol1?: number | null,
  vol2?: number | null
) => {
  if (vol1 === null || vol1 === undefined || vol2 === null || vol2 === undefined) return false
  const delta = Math.abs(vol1 - vol2)
  return delta <= volume_amount_threshold || delta / vol1 <= volume_percent_threshold
}

const isDateMatch = ({ minute_threshold }: Gravitate.EbolConfig, date1?: string | null, date2?: string | null) =>
  date1 !== null &&
  date1 !== undefined &&
  date2 !== null &&
  date2 !== undefined &&
  Math.abs(dayjs(date1).diff(dayjs(date2), 'minutes')) < minute_threshold

type RowType = components['schemas']['EBolResponse'] & { date_local?: string }
type RowDetailType = RowType['details'][0] & { parent?: RowType }

type BolObjectDetailsProps = {
  title?: string
  subtitle?: string
  isLoading?: boolean
  bolData?: Gravitate.ShortBol
  ebolData?: RowType[]
  onEbolSelected?: (ebol: Gravitate.Ebol) => void
  isReadonly?: boolean
}

export const EbolTable = ({
  title = 'eBOLs',
  subtitle,
  isLoading,
  bolData,
  ebolData = [],
  onEbolSelected,
  isReadonly,
}: BolObjectDetailsProps) => {
  const { config, isLoading: configLoading } = useModelConfig()

  const [showSourceData, setShowSourceData] = useState(false)

  const context = useMemo(
    () => ({
      isReadonly,
      showSourceData,
    }),
    [isReadonly, showSourceData]
  )

  //note: this is a weird hack to make ag-grid update the detailRows. nothing else works
  const rowData = useMemo(() => ebolData.map((x) => ({ ...x, context })), [ebolData, context])

  const volumeEqualityFn = useCallback(
    (v1?: number | null, v2?: number | null) => {
      if (!config?.ebol_config) return v1 === v2
      return isVolumeMatch(config.ebol_config, v1, v2)
    },
    [config]
  )

  const dateEqualityFn = useCallback(
    (v1?: string | null, v2?: string | null) => isDateMatch(config?.ebol_config ?? { minute_threshold: 0 }, v1, v2),
    [config]
  )

  const parentColumns = useMemo(() => {
    let builder = new ColDefBuilder<RowType>({
      suppressMovable: true,
      sortable: false,
      enableRowGroup: false,
      cellStyle: { padding: '0px' },
    })
      .defineColumn('_id', { hide: true, initialHide: true })
      .defineColumn('details.length', {
        headerName: 'Details',
        cellRenderer: 'agGroupCellRenderer',
        maxWidth: 80,
        valueFormatter: () => '',
        suppressMenu: true,
        cellStyle: undefined,
      })
      .defineColumn('bol_number', {
        headerName: 'BOL #',
        maxWidth: 110,
        cellRenderer: (p) => <ComparisonCell {...p} previousValue={bolData?.bol_number} />,
      })
      .defineColumn('terminal', {
        headerName: 'Terminal/Origin',
        cellRenderer: (p) => (
          <TerminalCell
            {...p}
            sourceSystem={p.data?.source_system ?? ''}
            previousValue={bolData?.terminal}
            showSource={showSourceData}
            preventAdding={isReadonly}
          />
        ),
        valueFormatter: (p) => p.value?.id_names?.map((x) => x.name)?.join(', ') || 'Unmapped',
      })
      .defineColumn('supplier', {
        headerName: 'Supplier',
        cellRenderer: (p) => (
          <SupplierCell
            {...p}
            sourceSystem={p.data?.source_system ?? ''}
            previousValue={bolData?.details?.map((x) => x.supplier).join('-MySeparatorCode-')}
            isEqual={(v1, v2) => !!v2?.split('-MySeparatorCode-')?.some((x) => v1 === x)}
            showSource={showSourceData}
            preventAdding={isReadonly}
          />
        ),
        valueFormatter: (p) => p.value?.id_names?.map((x) => x.name)?.join(', ') || 'Unmapped',
      })
      .defineColumn('date', {
        headerName: 'Date/Time',
        maxWidth: 155,
        cellRenderer: (p) => <ComparisonCell<string> {...p} previousValue={bolData?.date} isEqual={dateEqualityFn} />,
        valueGetter: ({ data }) => (data?.date ? formatters.DATE_TIME(data?.date, data?.date_local, true) : undefined),
      })
    if (!isReadonly) {
      builder = builder.defineUnknownColumn({
        colId: 'delete',
        pinned: 'right',
        maxWidth: 70,
        resizable: false,
        cellRenderer: DeleteEbolButton,
        suppressMenu: true,
        cellStyle: undefined,
      })
    }
    return builder.columns
  }, [bolData, dateEqualityFn, showSourceData, isReadonly])

  const detailCellRendererParams = useMemo<Partial<IDetailCellRendererParams<RowType, RowDetailType>>>(
    () => ({
      detailGridOptions: {
        columnDefs: new ColDefBuilder<RowDetailType>({
          suppressMovable: true,
          suppressMenu: true,
          sortable: false,
          enableRowGroup: false,
          cellStyle: { padding: '0px' },
        })
          .defineColumn('product', {
            headerName: 'Product Name',
            cellRenderer: (p) => (
              <ProductCell
                {...p}
                showSource={showSourceData}
                sourceSystem={p.data?.parent?.source_system ?? ''}
                preventAdding={isReadonly}
              />
            ),
          })
          .defineColumn('net_volume', {
            headerName: 'Net QTY',
            maxWidth: 130,
            cellRenderer: (p) => (
              <ComparisonCell<number>
                {...p}
                isEqual={volumeEqualityFn}
                previousValue={bolData?.details?.[p.node.rowIndex ?? 0]?.net_volume}
              />
            ),
          })
          .defineColumn('gross_volume', {
            headerName: 'Gross QTY',
            maxWidth: 130,
            cellRenderer: (p) => (
              <ComparisonCell
                {...p}
                isEqual={volumeEqualityFn}
                previousValue={bolData?.details?.[p.node.rowIndex ?? 0]?.gross_volume}
              />
            ),
          }).columns,
        rowHeight: 75,
        suppressRowTransform: true,
      },
      refreshStrategy: 'everything',
    }),
    [isReadonly, bolData, showSourceData, volumeEqualityFn]
  )

  const onSelectionChanged = useCallback(
    ({ api }: SelectionChangedEvent<RowType>) => {
      const selectedRow = api.getSelectedRows()[0]
      if (!selectedRow || !ebolData) return
      const ebolSelected = ebolData.find((x) => x._id === selectedRow._id)
      if (ebolSelected) onEbolSelected?.(ebolSelected)
    },
    [ebolData, onEbolSelected]
  )

  return (
    <BolBaseTable
      controlBarProps={{
        title,
        actionButtons: (
          <Horizontal>
            <Tooltip title='View eBOL data and source Id'>
              <Switch value={showSourceData} onChange={setShowSourceData} />
            </Tooltip>
            <Texto appearance='hint' className='px-2'>
              Source Data
            </Texto>
          </Horizontal>
        ),
        customSearchBar: (
          <Horizontal justifyContent='center' alignItems='center' style={{ flexGrow: 1 }}>
            {subtitle ? <Texto>{subtitle}</Texto> : undefined}
          </Horizontal>
        ),
      }}
      columnDefs={parentColumns}
      detailCellRendererParams={detailCellRendererParams}
      rowData={rowData}
      loading={isLoading || configLoading}
      onSelectionChanged={onSelectionChanged}
      agPropOverrides={{
        suppressRowTransform: true,
        getRowId: (p) => p.data._id ?? crypto.randomUUID(),
        rowSelection: 'single',
        rowHeight: 75,
        getRowHeight: ({ node, api, data }) => {
          if (!node?.detail) return undefined
          const { rowHeight, headerHeight } = api.getSizesForCurrentTheme()
          return rowHeight * (data?.details?.length ?? 0) + headerHeight
        },
        suppressClickEdit: true,
      }}
    />
  )
}
